import React, { memo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import styles from 'Components/PdfFormTemplates/pdfFormTemplates.module.css';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { createPdfFormTemplate } from './actions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function CreateTemplateModal({ isOpen, onClose }: Props) {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped?.type === 'application/pdf') {
      setFile(dropped);
    }
  }, []);

  const onFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected?.type === 'application/pdf') setFile(selected);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!name.trim() || !file || !companyId) return;
    setSubmitting(true);

    const formData = new FormData();
    formData.append('company_id', companyId);
    formData.append('name', name.trim());
    formData.append('pdf_file', file);

    await dispatch(createPdfFormTemplate(formData) as any);
    setSubmitting(false);
    setName('');
    setFile(null);
  }, [name, file, companyId, dispatch]);

  const handleClose = useCallback(() => {
    setName('');
    setFile(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.modalOverlay} onClick={handleClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2>Create PDF Form Template</h2>

        <div className={styles.formGroup}>
          <label>Template Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Authorization to Work"
          />
        </div>

        <div className={styles.formGroup}>
          <label>PDF File</label>
          <div
            className={`${styles.dropzone} ${file ? styles.dropzoneFile : ''}`}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => e.preventDefault()}
          >
            {file ? (
              <span>{file.name}</span>
            ) : (
              <span>Drop a PDF file here, or use the button below</span>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept=".pdf" onChange={onFileSelect} style={{ display: 'none' }} />
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => fileInputRef.current?.click()}
            style={{ marginTop: 8, width: '100%' }}
          >
            {file ? 'Choose a Different File' : 'Choose PDF File'}
          </button>
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={styles.btnSecondary} onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSubmit}
            disabled={!name.trim() || !file || submitting}
          >
            {submitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

const CreateTemplateModalMemo = memo(CreateTemplateModal, areEqual);
export { CreateTemplateModalMemo as CreateTemplateModal };
